import { Menu, Search, X } from "lucide-react";
import { useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";

import { useAuth } from "../../lib/auth";
import { initials } from "../../lib/format";
import NotificationsDropdown from "./NotificationsDropdown";

/**
 * Top app bar — sticky on every page.
 *
 * Search field: when the user types and presses Enter (or after 350ms idle),
 * navigates to `/candidats?q=<query>` so the Candidates list applies the
 * filter on top of the existing visible list (works on names, phone, city,
 * position).
 *
 * Mobile: shows a hamburger button + a compact search icon that expands into
 * a full-width search overlay when tapped. The Bell + avatar stay visible.
 */
export default function Topbar({ unreadCount = 0, onOpenMobileNav, onUnreadChange }) {
    const { user } = useAuth();
    const navigate = useNavigate();
    const location = useLocation();
    const [searchParams] = useSearchParams();
    const initialQ = location.pathname.startsWith("/candidats") ? (searchParams.get("q") || "") : "";
    const [q, setQ] = useState(initialQ);
    const [mobileSearchOpen, setMobileSearchOpen] = useState(false);
    const mobileInputRef = useRef(null);

    // Keep the input in sync when the URL `q` changes externally
    useEffect(() => {
        if (location.pathname.startsWith("/candidats")) {
            setQ(searchParams.get("q") || "");
        }
    }, [location.pathname, searchParams]);

    useEffect(() => {
        if (mobileSearchOpen && mobileInputRef.current) {
            mobileInputRef.current.focus();
        }
    }, [mobileSearchOpen]);

    function submit(e) {
        if (e) e.preventDefault();
        const trimmed = q.trim();
        setMobileSearchOpen(false);
        if (trimmed.length === 0) {
            navigate("/candidats");
            return;
        }
        navigate(`/candidats?q=${encodeURIComponent(trimmed)}`);
    }

    function clear() {
        setQ("");
        navigate("/candidats");
    }

    return (
        <header className="sticky top-0 z-40 bg-shariti-cream/85 backdrop-blur-xl border-b border-shariti-gray">
            <div className="flex items-center gap-2 md:gap-3 px-3 md:px-8 h-14 md:h-16">
                {/* === Mobile-only: hamburger menu button === */}
                <button
                    type="button"
                    onClick={onOpenMobileNav}
                    data-testid="topbar-mobile-menu"
                    aria-label="Ouvrir le menu"
                    className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange transition shrink-0"
                >
                    <Menu className="h-4 w-4" />
                </button>

                {/* === Mobile-only: brand label (only when search is closed) === */}
                {!mobileSearchOpen && (
                    <button
                        type="button"
                        onClick={() => navigate("/dashboard")}
                        data-testid="topbar-mobile-brand"
                        title="Retour au cockpit"
                        className="lg:hidden inline-flex items-center gap-2 min-w-0 px-1 group"
                    >
                        <div className="h-8 w-8 rounded-lg bg-gradient-to-br from-shariti-orange to-shariti-orange-light flex items-center justify-center shrink-0 shadow-btn">
                            <span className="text-white font-black text-xs">P</span>
                        </div>
                        <div className="text-left leading-none">
                            <div className="font-extrabold tracking-tight text-shariti-black text-sm group-hover:text-shariti-orange transition">
                                PREREC
                            </div>
                            <div className="text-[8px] font-semibold uppercase tracking-[0.16em] text-slate-400 mt-0.5">
                                by Shariti
                            </div>
                        </div>
                    </button>
                )}

                {/* === Desktop search form === */}
                <form
                    onSubmit={submit}
                    className="hidden lg:flex items-center gap-2 bg-white border border-shariti-gray rounded-full px-4 h-10 w-full max-w-md text-sm focus-within:border-shariti-orange focus-within:ring-2 focus-within:ring-shariti-orange/10 transition"
                >
                    <Search className="h-4 w-4 text-slate-400 shrink-0" />
                    <input
                        data-testid="topbar-search"
                        value={q}
                        onChange={(e) => setQ(e.target.value)}
                        placeholder="Rechercher un candidat (nom, ville, téléphone…)"
                        className="bg-transparent outline-none flex-1 placeholder:text-slate-400 text-shariti-black font-medium min-w-0"
                    />
                    {q && (
                        <button
                            type="button"
                            onClick={clear}
                            data-testid="topbar-search-clear"
                            title="Effacer"
                            className="h-6 w-6 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-shariti-red hover:bg-red-50 transition shrink-0"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    )}
                    <kbd className="hidden md:inline-flex text-[9px] font-bold uppercase tracking-widest text-slate-400 px-1.5 py-0.5 rounded bg-shariti-cream border border-shariti-gray">
                        Entrée
                    </kbd>
                </form>

                {/* === Mobile expanded search === */}
                {mobileSearchOpen && (
                    <form
                        onSubmit={submit}
                        className="lg:hidden flex-1 flex items-center gap-2 bg-white border border-shariti-orange ring-2 ring-shariti-orange/15 rounded-full px-3 h-10 text-sm transition"
                    >
                        <Search className="h-4 w-4 text-shariti-orange shrink-0" />
                        <input
                            ref={mobileInputRef}
                            data-testid="topbar-search-mobile"
                            value={q}
                            onChange={(e) => setQ(e.target.value)}
                            placeholder="Nom, ville, téléphone…"
                            className="bg-transparent outline-none flex-1 placeholder:text-slate-400 text-shariti-black font-medium min-w-0"
                        />
                        <button
                            type="button"
                            onClick={() => { setMobileSearchOpen(false); setQ(initialQ); }}
                            data-testid="topbar-search-mobile-close"
                            aria-label="Fermer la recherche"
                            className="h-7 w-7 inline-flex items-center justify-center rounded-full text-slate-400 hover:text-shariti-red transition shrink-0"
                        >
                            <X className="h-3.5 w-3.5" />
                        </button>
                    </form>
                )}

                <div className="ml-auto flex items-center gap-1.5 md:gap-3 shrink-0">
                    {/* Mobile-only search trigger */}
                    {!mobileSearchOpen && (
                        <button
                            type="button"
                            onClick={() => setMobileSearchOpen(true)}
                            data-testid="topbar-search-toggle"
                            aria-label="Rechercher"
                            className="lg:hidden h-10 w-10 inline-flex items-center justify-center rounded-full bg-white border border-shariti-gray text-slate-600 hover:text-shariti-orange transition"
                        >
                            <Search className="h-4 w-4" />
                        </button>
                    )}

                    <NotificationsDropdown
                        unreadCount={unreadCount}
                        onUnreadChange={onUnreadChange}
                    />
                    <div className="hidden md:flex items-center gap-2 pl-2 pr-3 h-10 rounded-full bg-white border border-shariti-gray">
                        {user?.avatar_url ? (
                            <img src={user.avatar_url} alt="" className="h-7 w-7 rounded-full object-cover" />
                        ) : (
                            <div className="h-7 w-7 rounded-full bg-shariti-orange-soft text-shariti-orange font-extrabold text-xs flex items-center justify-center">
                                {initials(user?.name)}
                            </div>
                        )}
                        <span className="text-xs font-bold text-shariti-black">{user?.name}</span>
                    </div>
                </div>
            </div>
        </header>
    );
}
